/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { endSession } from '../../store/modules/auth/sessionCleanup';
import { notifyApiError } from '../../utils/feedback';
import {
   getUser,
   updateUser,
   deleteUser,
   setUserPdvs,
} from '../../services/users/usersApi';
import { listRoles } from '../../services/users/rolesApi';
import { listPdvs } from '../../services/inventory/pdvsApi';

import PdvSelector from '../../components/UniqueCards/PdvSelector';
import Loading from '../../components/Loading';
import Modal from '../../components/Modal/Modal';
import StatusBadge from '../../components/StatusBadge';
import usePermissions from '../../components/Menu/MenuItems/hooks/usePermissions';

import { Container } from '../../styles/layout/Container';
import { EmptyState } from '../../styles/feedback/EmptyState';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../styles/layout/PageHeader';

import {
   Section,
   SectionHeader,
   SectionTitle,
   SectionBody,
} from '../../styles/layout/Section';
import { Field } from '../../styles/forms/Field';
import { FormGrid } from '../../styles/layout/FormGrid';
import { ActionBar } from '../../styles/layout/ActionBar';
import { EntityActionBar, ActionBarSpacer } from '../../styles/ui/ActionBar';
import {
   PrimaryButton,
   GhostButton,
   DangerButton,
} from '../../styles/ui/Button';
import { FieldError, FieldHint } from '../../styles/ui/Composer';

// ---------------------------------------------------------------------------
// Usuários — ficha da conta (handoff 0110).
//
// Reescrita em 2026-09-14 sobre o módulo Gen3: `GET /users/:id` (a rota
// `/show/:id` morreu), `PUT /users/:id` agora exige `password_confirm` junto
// da senha, e a exclusão (`DELETE /users/:id`) passou a existir de verdade —
// antes `DELETE /users` deletava o PRÓPRIO logado e quebrava em FK.
//
// RBAC por verbo: entrar exige `iam:user:read` (gate da rota); editar e
// conceder PDV exigem `iam:user:update`; excluir exige `iam:user:delete`. Sem
// `update` a ficha vira leitura — os campos ficam desabilitados em vez de
// sumir, porque a informação continua valendo para quem só consulta.
//
// Conta EXCLUÍDA continua abrindo (é como se audita quem saiu, a partir do
// filtro `status=inactive` da listagem), mas em modo somente-leitura: não há
// restore no contrato.
// ---------------------------------------------------------------------------

const CardsRow = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 14px;
   align-items: start;

   @media (max-width: 900px) {
      grid-template-columns: 1fr;
   }
`;

// Faixa de estado da ficha. `danger` = conta excluída; `warning` = ficha em
// modo leitura por falta de `iam:user:update`. Local de propósito: é aviso de
// domínio, não primitivo do DS (se um segundo módulo pedir o mesmo, aí sim
// promove — FC7 §H.4).
const Banner = styled.div`
   margin-bottom: 14px;
   padding: 12px 14px;
   border-radius: 10px;
   font-size: 13px;
   line-height: 1.5;
   border: 1px solid
      ${({ $tone }) =>
         $tone === 'warning' ? 'rgba(133,79,11,0.25)' : 'rgba(171,32,32,0.25)'};
   background: ${({ $tone }) =>
      $tone === 'warning' ? 'rgba(133,79,11,0.07)' : 'rgba(171,32,32,0.07)'};
   color: ${({ $tone }) => ($tone === 'warning' ? '#854F0B' : '#8a1f1f')};
`;

const ModalText = styled.p`
   margin: 0 0 12px;
   font-size: 13.5px;
   line-height: 1.55;
   color: rgba(0, 0, 0, 0.75);
`;

const ModalWarning = styled.p`
   margin: 0;
   padding: 10px 12px;
   border-radius: 8px;
   border: 1px solid rgba(220, 38, 38, 0.22);
   background: rgba(220, 38, 38, 0.06);
   color: #8e1b1b;
   font-size: 12.5px;
   line-height: 1.5;
`;

function formatDateTimeBr(value) {
   if (!value) return '—';
   const date = new Date(value);
   if (Number.isNaN(date.getTime())) return '—';
   return date.toLocaleString('pt-BR');
}

export default function UserAdminEdit() {
   const navigate = useNavigate();
   const location = useLocation();
   const dispatch = useDispatch();
   const { id } = useParams();

   const { hasPermission } = usePermissions();
   const canUpdate = hasPermission('iam:user:update');
   const canDelete = hasPermission('iam:user:delete');

   // Trocar senha revoga TODAS as sessões do usuário alvo (handoff 0097 §4).
   // Quando o alvo é o próprio admin, a sessão que está usando a tela morre
   // junto — sem isto ele ficaria numa tela que só toma 401 no próximo clique.
   // É também o caso que o backend recusa no delete (`self_delete_forbidden`).
   const currentUserId = useSelector((state) => state.auth.user?.id);
   const isEditingSelf = String(currentUserId ?? '') === String(id);

   const [user, setUser] = useState(null);
   const [roles, setRoles] = useState([]);
   const [rolesDenied, setRolesDenied] = useState(false);
   // Começa TRUE: no primeiro render o efeito de carga ainda não rodou, e com
   // `false` a tela piscaria o "usuário não encontrado" antes de buscar.
   const [isLoading, setIsLoading] = useState(true);
   const [isSubmitting, setIsSubmitting] = useState(false);

   const [allPdvs, setAllPdvs] = useState([]);
   const [selectedPdvIds, setSelectedPdvIds] = useState([]);
   const [isSavingPdvs, setIsSavingPdvs] = useState(false);

   const [isDeleteOpen, setIsDeleteOpen] = useState(false);
   const [isDeleting, setIsDeleting] = useState(false);

   const [form, setForm] = useState({
      nome: '',
      role_id: '',
      password: '',
      password_confirm: '',
   });
   const [fieldErrors, setFieldErrors] = useState({});

   const isDeleted = Boolean(user) && user.is_active === false;
   const isReadOnly = !canUpdate || isDeleted;

   const rolesById = useMemo(() => {
      const map = new Map();
      roles.forEach((role) => map.set(Number(role.id), role));
      return map;
   }, [roles]);

   // Preenche estado de tela a partir do usuário recarregado (load, save, PDVs).
   const applyUser = useCallback((nextUser) => {
      if (!nextUser) return;
      setUser(nextUser);
      setForm((prev) => ({
         ...prev,
         nome: nextUser.nome ?? '',
         role_id: nextUser.role_id ? String(nextUser.role_id) : '',
         password: '',
         password_confirm: '',
      }));
      if (Array.isArray(nextUser.pdvs)) {
         setSelectedPdvIds(nextUser.pdvs.map((pdv) => pdv.id));
      }
   }, []);

   useEffect(() => {
      let mounted = true;

      async function load() {
         try {
            setIsLoading(true);

            // 1) Usuário primeiro — é o que a tela precisa para existir.
            const loadedUser = await getUser(id);
            if (!mounted) return;
            applyUser(loadedUser);

            // 2) Catálogos depois: falhar num deles degrada a tela, não a
            //    impede. O papel continua legível pelo id, e os PDVs viram uma
            //    seção vazia com aviso do próprio PdvSelector.
            // `GET /roles` exige `iam:role:read` — chave de OUTRO módulo, que
            // quem administra usuários pode não ter. A flag impede que esse
            // 403 troque a ficha inteira por `/403` com reload; sem catálogo,
            // o select fica desabilitado exibindo o papel atual (que veio no
            // payload do próprio usuário), e salvar preserva o vínculo.
            try {
               const rolesList = await listRoles({
                  skipForbiddenRedirect: true,
               });
               if (mounted) setRoles(rolesList);
            } catch (err) {
               if (!mounted) return;
               if (err?.kind === 'forbidden') {
                  setRolesDenied(true);
               } else {
                  notifyApiError(err, {
                     fallback: 'Erro ao carregar os papéis.',
                  });
               }
            }

            try {
               const pdvsList = await listPdvs();
               if (mounted) setAllPdvs(pdvsList);
            } catch (err) {
               notifyApiError(err, { fallback: 'Erro ao carregar os PDVs.' });
            }
         } catch (err) {
            notifyApiError(err, { fallback: 'Erro ao carregar o usuário.' });
         } finally {
            if (mounted) setIsLoading(false);
         }
      }

      load();
      return () => {
         mounted = false;
      };
   }, [id, applyUser]);

   const onChange = (e) => {
      const { name, value } = e.target;
      setForm((prev) => ({ ...prev, [name]: value }));
      setFieldErrors((prev) => {
         if (!prev[name]) return prev;
         const next = { ...prev };
         delete next[name];
         return next;
      });
   };

   // Espelha o backend só para poupar round-trip; a validação que manda é a de
   // lá.
   const validate = () => {
      const errors = {};

      if (!form.nome || form.nome.trim().length < 3) {
         errors.nome = 'Informe ao menos 3 caracteres.';
      }
      if (!form.role_id) {
         errors.role_id = 'Selecione o papel de acesso.';
      }
      if (form.password || form.password_confirm) {
         if (form.password.length < 6 || form.password.length > 50) {
            errors.password = 'A senha precisa ter entre 6 e 50 caracteres.';
         }
         if (form.password !== form.password_confirm) {
            errors.password_confirm = 'A confirmação não confere com a senha.';
         }
      }

      return errors;
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      if (isReadOnly) return;

      const localErrors = validate();
      if (Object.keys(localErrors).length) {
         setFieldErrors(localErrors);
         return;
      }

      try {
         setIsSubmitting(true);
         setFieldErrors({});

         const { user: savedUser, message } = await updateUser(id, {
            nome: form.nome.trim(),
            roleId: form.role_id,
            password: form.password || undefined,
            passwordConfirm: form.password_confirm || undefined,
         });

         const changedOwnPassword = Boolean(form.password) && isEditingSelf;

         if (changedOwnPassword) {
            toast.success(
               'Senha alterada com sucesso. Entre novamente com a nova senha.'
            );
            // Sessão morta no backend — encerrar localmente é o único desfecho.
            endSession(dispatch, { delayMs: 1200 });
            return;
         }

         applyUser(savedUser);
         toast.success(message);
      } catch (err) {
         const apiFieldErrors = err?.fieldErrors ?? {};
         if (Object.keys(apiFieldErrors).length) {
            setFieldErrors(apiFieldErrors);
         } else {
            notifyApiError(err, { fallback: 'Erro ao salvar usuário.' });
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   const handleSavePdvs = async () => {
      if (isReadOnly) return;

      try {
         setIsSavingPdvs(true);
         // Substituição TOTAL do conjunto: o backend devolve o usuário já
         // recarregado, então não há segundo GET.
         const { user: savedUser, message } = await setUserPdvs(
            id,
            selectedPdvIds
         );
         applyUser(savedUser);
         toast.success(message);
      } catch (err) {
         notifyApiError(err, { fallback: 'Erro ao salvar PDVs.' });
      } finally {
         setIsSavingPdvs(false);
      }
   };

   // "Voltar" restaura a listagem exatamente onde o operador estava (filtros,
   // situação e página vivem na URL) — `navegacao.md` 1.6. Deep-link direto na
   // ficha não tem histórico, e aí cai no caminho da lista.
   const handleBack = useCallback(() => {
      if (location.key !== 'default') navigate(-1);
      else navigate('/users');
   }, [location.key, navigate]);

   const handleDelete = async () => {
      try {
         setIsDeleting(true);
         await deleteUser(id);
         setIsDeleteOpen(false);
         toast.success('Usuário excluído. O e-mail está livre para um novo cadastro.');
         handleBack();
      } catch (err) {
         notifyApiError(err, { fallback: 'Erro ao excluir usuário.' });
      } finally {
         setIsDeleting(false);
      }
   };

   const roleLabel = (() => {
      if (user?.role?.name) return user.role.name;
      if (!form.role_id) return '—';
      const role = rolesById.get(Number(form.role_id));
      return role?.name ?? `#${form.role_id}`;
   })();

   const isBusy = isLoading || isSubmitting;

   // 404 (ou falha na carga): sem usuário não há ficha. O formulário vazio com
   // botão "Salvar" seria pior — o operador preencheria para tomar erro.
   if (!isLoading && !user) {
      return (
         <Container>
            <EmptyState
               title="Usuário não encontrado"
               description="A conta pode ter sido excluída e removida do sistema, ou o endereço está incorreto."
               action={
                  <GhostButton type="button" onClick={handleBack}>
                     Voltar
                  </GhostButton>
               }
            />
         </Container>
      );
   }

   return (
      <Container>
         <Loading isLoading={isLoading || isDeleting} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>
                  {user?.nome || 'Usuário'}{' '}
                  <StatusBadge
                     variant={user && !isDeleted ? 'green' : 'red'}
                     size="sm"
                  >
                     {user && !isDeleted ? 'Ativo' : 'Excluído'}
                  </StatusBadge>
               </PageTitle>
               <PageSubtitle>
                  {user?.email ?? '—'} • Papel: {roleLabel}
               </PageSubtitle>
            </PageHeaderText>

            <PageHeaderActions>
               <EntityActionBar>
                  {/* Destrutivo isolado na ponta esquerda (FC7 §G.5). Some para
                      quem não tem a chave e para a própria conta — o backend
                      recusa com `self_delete_forbidden`, e oferecer o botão só
                      para tomar 422 é armadilha. */}
                  {canDelete && !isDeleted && !isEditingSelf && (
                     <>
                        <DangerButton
                           type="button"
                           onClick={() => setIsDeleteOpen(true)}
                           disabled={isBusy}
                        >
                           Excluir usuário
                        </DangerButton>
                        <ActionBarSpacer />
                     </>
                  )}

                  <GhostButton
                     type="button"
                     onClick={handleBack}
                     disabled={isSubmitting}
                  >
                     Voltar
                  </GhostButton>

                  {!isReadOnly && (
                     <PrimaryButton
                        type="button"
                        onClick={handleSubmit}
                        disabled={isBusy}
                     >
                        {isSubmitting ? 'Salvando...' : 'Salvar'}
                     </PrimaryButton>
                  )}
               </EntityActionBar>
            </PageHeaderActions>
         </PageHeader>

         {isDeleted && (
            <Banner $tone="danger">
               <strong>
                  Conta excluída em {formatDateTimeBr(user.deleted_at)}.
               </strong>{' '}
               O acesso foi revogado e o e-mail <strong>{user.email}</strong> já
               pode ser usado num novo cadastro. A exclusão é definitiva — não há
               restauração; para devolver o acesso, crie uma conta nova.
            </Banner>
         )}

         {!canUpdate && !isDeleted && (
            <Banner $tone="warning">
               Você tem acesso de leitura a esta ficha. Editar dados, senha ou
               PDVs exige a permissão <code>iam:user:update</code>.
            </Banner>
         )}

         <form onSubmit={handleSubmit}>
            <CardsRow>
               <Section>
                  <SectionHeader>
                     <SectionTitle>Dados</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                     <FormGrid>
                        <Field>
                           <label htmlFor="nome">Nome</label>
                           <input
                              id="nome"
                              name="nome"
                              value={form.nome}
                              onChange={onChange}
                              disabled={isBusy || isReadOnly}
                              aria-invalid={!!fieldErrors.nome || undefined}
                           />
                           {fieldErrors.nome && (
                              <FieldError>{fieldErrors.nome}</FieldError>
                           )}
                        </Field>

                        <Field>
                           <label htmlFor="email">E-mail</label>
                           <input
                              id="email"
                              name="email"
                              value={user?.email ?? ''}
                              disabled
                              title="Alteração de e-mail não é permitida."
                              readOnly
                           />
                           <FieldHint>
                              Chave de login — imutável. Para trocar, exclua a
                              conta e crie outra.
                           </FieldHint>
                        </Field>

                        <Field>
                           <label htmlFor="role_id">Papel</label>
                           <select
                              id="role_id"
                              name="role_id"
                              value={form.role_id}
                              onChange={onChange}
                              disabled={isBusy || isReadOnly || rolesDenied}
                              aria-invalid={!!fieldErrors.role_id || undefined}
                           >
                              <option value="">Selecione</option>
                              {/* Sem catálogo (403 em /roles), o papel atual
                                  ainda precisa aparecer: o valor vem da própria
                                  ficha, e salvar preserva o vínculo. */}
                              {rolesDenied && form.role_id && (
                                 <option value={form.role_id}>
                                    {roleLabel}
                                 </option>
                              )}
                              {roles.map((role) => (
                                 <option key={role.id} value={String(role.id)}>
                                    {role.name}
                                 </option>
                              ))}
                           </select>
                           {fieldErrors.role_id && (
                              <FieldError>{fieldErrors.role_id}</FieldError>
                           )}
                           {rolesDenied && (
                              <FieldHint>
                                 Trocar o papel exige a permissão{' '}
                                 <code>iam:role:read</code>. Os demais campos
                                 continuam editáveis.
                              </FieldHint>
                           )}
                        </Field>

                        <Field>
                           <label htmlFor="created_at">Criada em</label>
                           <input
                              id="created_at"
                              value={formatDateTimeBr(user?.created_at)}
                              disabled
                              readOnly
                           />
                        </Field>
                     </FormGrid>
                  </SectionBody>
               </Section>

               <Section>
                  <SectionHeader>
                     <SectionTitle>Senha (opcional)</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                     {/* O operador precisa saber o efeito ANTES de digitar: a
                         troca derruba todas as sessões do usuário alvo. */}
                     <p
                        style={{
                           margin: '0 0 12px',
                           fontSize: 13,
                           lineHeight: 1.5,
                           color: '#666',
                        }}
                     >
                        {isEditingSelf
                           ? 'Preencher a senha desconecta você de todos os dispositivos, inclusive deste.'
                           : 'Preencher a senha desconecta este usuário de todos os dispositivos.'}
                     </p>

                     <FormGrid>
                        <Field>
                           <label htmlFor="password">Nova senha</label>
                           <input
                              id="password"
                              name="password"
                              type="password"
                              value={form.password}
                              onChange={onChange}
                              disabled={isBusy || isReadOnly}
                              autoComplete="new-password"
                              aria-invalid={!!fieldErrors.password || undefined}
                           />
                           {fieldErrors.password && (
                              <FieldError>{fieldErrors.password}</FieldError>
                           )}
                        </Field>

                        <Field>
                           <label htmlFor="password_confirm">
                              Confirmar senha
                           </label>
                           <input
                              id="password_confirm"
                              name="password_confirm"
                              type="password"
                              value={form.password_confirm}
                              onChange={onChange}
                              disabled={isBusy || isReadOnly}
                              autoComplete="new-password"
                              aria-invalid={
                                 !!fieldErrors.password_confirm || undefined
                              }
                           />
                           {fieldErrors.password_confirm && (
                              <FieldError>
                                 {fieldErrors.password_confirm}
                              </FieldError>
                           )}
                        </Field>
                     </FormGrid>
                  </SectionBody>
               </Section>
            </CardsRow>
         </form>

         <Section style={{ marginTop: '14px' }}>
            <SectionHeader>
               <SectionTitle>
                  Pontos de venda — acesso do usuário ({selectedPdvIds.length}/
                  {allPdvs.length})
               </SectionTitle>
            </SectionHeader>

            <SectionBody>
               <p
                  style={{
                     marginBottom: '16px',
                     color: '#666',
                     fontSize: '14px',
                  }}
               >
                  Selecione os PDVs que este usuário poderá acessar. O usuário só
                  poderá visualizar e interagir com movimentos, produtos e
                  estoques dos PDVs selecionados abaixo.
               </p>

               <PdvSelector
                  allPdvs={allPdvs}
                  selectedPdvIds={selectedPdvIds}
                  onChange={setSelectedPdvIds}
                  disabled={isSavingPdvs || isLoading || isReadOnly}
               />

               {!isReadOnly && (
                  <ActionBar style={{ marginTop: '20px' }}>
                     <PrimaryButton
                        type="button"
                        onClick={handleSavePdvs}
                        disabled={isSavingPdvs || isLoading}
                     >
                        {isSavingPdvs ? 'Salvando PDVs...' : 'Salvar PDVs'}
                     </PrimaryButton>
                  </ActionBar>
               )}
            </SectionBody>
         </Section>

         <Modal
            isOpen={isDeleteOpen}
            title="Excluir usuário"
            subtitle={user?.email}
            onClose={() => setIsDeleteOpen(false)}
            maxWidth="540px"
            footer={
               <>
                  <GhostButton
                     type="button"
                     onClick={() => setIsDeleteOpen(false)}
                     disabled={isDeleting}
                  >
                     Voltar
                  </GhostButton>
                  <DangerButton
                     type="button"
                     onClick={handleDelete}
                     disabled={isDeleting}
                  >
                     {isDeleting ? 'Excluindo...' : 'Excluir definitivamente'}
                  </DangerButton>
               </>
            }
         >
            <ModalText>
               <strong>{user?.nome}</strong> perde o acesso imediatamente: a
               sessão aberta dele para de valer no próximo clique, e ele some da
               listagem de ativos.
            </ModalText>
            <ModalWarning>
               A exclusão é <strong>definitiva</strong> — não existe restauração.
               O e-mail <strong>{user?.email}</strong> fica livre e pode ser
               usado num novo cadastro.
            </ModalWarning>
         </Modal>
      </Container>
   );
}
